import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ShippingSettings {
  baseShippingFee: number;
  freeShippingThreshold: number | null;
  progressBarEnabled: boolean;
  progressBarTextTemplate: string;
}

const DEFAULT_SETTINGS: ShippingSettings = {
  baseShippingFee: 59,
  freeShippingThreshold: null,
  progressBarEnabled: false,
  progressBarTextTemplate: 'Spend {remaining} SEK more for free shipping',
};

export function useShippingSettings() {
  const [settings, setSettings] = useState<ShippingSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('shipping_settings')
        .select('base_shipping_fee, free_shipping_threshold, progress_bar_enabled, progress_bar_text_template')
        .single();

      if (error) {
        console.error('Failed to fetch shipping settings:', error);
        return;
      }

      if (data) {
        setSettings({
          baseShippingFee: data.base_shipping_fee,
          freeShippingThreshold: data.free_shipping_threshold,
          progressBarEnabled: data.progress_bar_enabled,
          progressBarTextTemplate: data.progress_bar_text_template || DEFAULT_SETTINGS.progressBarTextTemplate,
        });
      }
    } catch (error) {
      console.error('Error fetching shipping settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateShipping = (subtotal: number): number => {
    if (settings.freeShippingThreshold !== null && subtotal >= settings.freeShippingThreshold) {
      return 0;
    }
    return settings.baseShippingFee;
  };

  const getProgressBarData = (subtotal: number) => {
    if (!settings.progressBarEnabled || settings.freeShippingThreshold === null) {
      return null;
    }

    if (subtotal >= settings.freeShippingThreshold) {
      return {
        progress: 100,
        remaining: 0,
        text: 'You qualify for free shipping!',
        isFreeShipping: true,
      };
    }

    const remaining = settings.freeShippingThreshold - subtotal;
    const progress = (subtotal / settings.freeShippingThreshold) * 100;

    return {
      progress,
      remaining,
      text: settings.progressBarTextTemplate.replace('{remaining}', remaining.toFixed(0)),
      isFreeShipping: false,
    };
  };

  return {
    settings,
    loading,
    calculateShipping,
    getProgressBarData,
  };
}